import { PAGE_SIZE } from "constants/constant";
import { Class, ContentType, Tag } from "constants/enum";
import * as contentful from "contentful";
import { Taxon, Blog } from "types/types";

const client = contentful.createClient({
  space: process.env.REACT_APP_CONTENTFUL_SPACE,
  accessToken: process.env.REACT_APP_CONTENTFUL_ACCESS_TOKEN,
  environment: process.env.REACT_APP_CONTENTFUL_ENV,
});

const OrderCreatedDESC = "-sys.createdAt";
const IndexASC = "fields.index";

const imgParams = (w = 600) => `?w=${w}&q=80`;

class ContentService {
  private parse(item: contentful.Entry<any>) {
    const {
      metadata,
      fields,
      sys: { createdAt },
    } = item;

    const tags = metadata.tags.map((tag) => tag.sys.id as Tag);
    const image = (fields as any).image.fields.file.url + imgParams(800);

    return {
      ...(fields as any),
      image,
      createdAt: new Date(createdAt),
      tags,
    };
  }

  private parseTaxon(item: contentful.Entry<any>): Taxon {
    const {
      fields,
      sys: { createdAt },
    } = item;

    const images = fields["images"].map(
      (image) => image.fields.file.url + imgParams(400),
    );
    return { ...fields, images, createdAt: new Date(createdAt) };
  }

  async get(): Promise<Blog[]> {
    const { items } = await client.getEntries({
      content_type: ContentType.BLOG,
      order: OrderCreatedDESC,
    });
    return items.map((item) => this.parse(item));
  }

  async getOneBySlug(slug: string): Promise<Blog> {
    const { items } = await client.getEntries({
      content_type: ContentType.BLOG,
      "fields.slug[in]": slug,
    });
    return this.parse(items[0]);
  }

  async listTaxons(
    skip = 0,
    limit = PAGE_SIZE,
    classes: Class[],
  ): Promise<[Taxon[], number]> {
    const { items, total } = await client.getEntries({
      content_type: ContentType.TAXON,
      order: IndexASC,
      skip,
      limit,
      "fields.class[in]": classes?.join(","),
    });
    return [items.map((item) => this.parseTaxon(item)), total];
  }

  async getStats(): Promise<{
    birds: number;
    butterflies: number;
    fauna: number;
  }> {
    const [birds, butterflies, fauna] = await Promise.all([
      client.getEntries({
        content_type: ContentType.TAXON,
        limit: 0,
        "fields.class[in]": Class.BIRD,
      }),
      client.getEntries({
        content_type: ContentType.TAXON,
        limit: 0,
        "fields.class[in]": Class.BUTTERFLY,
      }),
      client.getEntries({
        content_type: ContentType.TAXON,
        limit: 0,
        "fields.class[in]": [
          Class.MAMMAL,
          Class.AMPHIBIAN,
          Class.REPTILE,
          Class.INSECT,
        ].join(","),
      }),
    ]);
    return {
      birds: birds.total,
      butterflies: butterflies.total,
      fauna: fauna.total,
    };
  }
}

export const contentService = new ContentService();
